import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const trainers = [
  {
    img: "/assets/img/team/imil.png",
    name: "Imil Assaf",
    role: "International Advisor",
    bio: "Masters (Civil Engineering) · Guest Professor – Construction Engineering Management · 20+ years in AEC globally.",
    country: "USA",
    linkedin: "https://www.linkedin.com/in/imil-assaf-5bbb0253/", 
  },
  {
    img: "/assets/img/team/bhumika.png",
    name: "Bhumika Shah",
    role: "Senior Faculty",
    bio: "Masters (Construction Engineering Management) · Guest Professor – Construction Engineering Management.",
    country: "USA",
    linkedin: "https://www.linkedin.com/in/bhumika-shah-09b0b0123/", 
  },
  {
    img: "/assets/img/team/ram_bhosale.png",
    name: "Ram Bhosale",
    role: "IPDI Director",
    bio: "M.Arch (Sustainable Design) · Senior Professor – Sustainable Development.",
    country: "India",
    linkedin: "https://www.linkedin.com/in/ram-bhosale-44910813/",
  },
  {
    img: "/assets/img/team/jyoti_k.png",
    name: "Jyoti Kaulgud",
    role: "Senior BIM Specialist",
    bio: "B.Arch · Senior trainer for Revit Architecture and Navisworks.",
    country: "India",
    linkedin: "https://www.linkedin.com/in/jyoti-kaulgud-3b7a3551/",
  },
  {
    img: "/assets/img/team/shruti.png",
    name: "Shruti Dandage",
    role: "Guest Faculty",
    bio: "M.Arch (Digital Architecture) · Guest Faculty – Rhino and Grasshopper.",
    country: "India",
    linkedin: "https://www.linkedin.com/in/shruti-dandage-07096a57",
  },
];

// ── Updated: this card now invites trainers to APPLY — whole card is clickable,
// navigates to /trainer-application instead of opening a modal ──
const plusCard = {
  plus: true,
  name: "Join Our Trainer Team",
  role: "For Architects & Designers",
  bio: "Are you a working professional in architecture, interior design, or BIM? IPDI is onboarding trainers for the 2026 relaunch cohort.",
};

const allCards = [...trainers, plusCard];
const total = allCards.length; // 6

const DELAY = 2800;

// ─── Responsive breakpoints ────────────────────────────────────────────────
// Desktop behaviour (VISIBLE = 4, GAP = 20) is untouched.
// Mobile / tablet just get a smaller VISIBLE count so cards stay readable.
function getVisibleCount(width) {
  if (width <= 640) return 1; // mobile
  if (width <= 980) return 2; // tablet
  return 4; // desktop — unchanged
}
function getGap(width) {
  return width <= 640 ? 14 : 20; // desktop GAP stays 20, exactly as before
}

// Horizontal breathing room so the carousel wrapper doesn't touch the
// screen edges on mobile/tablet. Desktop stays at 0 (unchanged) since the
// section's own CSS padding (2.5rem) already handles desktop spacing.
function getSidePad(width) {
  if (width <= 640) return 16; // mobile
  if (width <= 980) return 24; // tablet
  return 0; // desktop — unchanged
}

// ─── Card Component ────────────────────────────────────────────────────────────
function TrainerCard({ t, cardW, onOpenApplication }) {
  const style = { flex: `0 0 ${cardW}px`, minWidth: 0 };

  if (t.plus) {
    return (
      <div
        className="tc tc-plus"
        style={{ ...style, cursor: "pointer" }}
        onClick={onOpenApplication}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onOpenApplication();
        }}
      >
        <div className="tc-plus-inner">
          <div className="tc-av">+</div>
          <div className="tc-name">{t.name}</div>
          <div className="tc-role">{t.role}</div>
          <p className="tc-bio">{t.bio}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tc" style={style}>
      <div className="tc-photo" style={{ aspectRatio: "1/1", overflow: "hidden" }}>
        <img
          src={t.img}
          alt={t.name}
          draggable={false}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", display: "block" }}
        />
      </div>
      <div className="tc-body">
        <div className="tc-name">{t.name}</div>
        <div className="tc-role">{t.role}</div>
        <p className="tc-bio">{t.bio}</p>
        <div className="tc-foot">
          <span className="tc-flag">{t.country}</span>
          <a
            href={t.linkedin || "#"}
            target="_blank"
            rel="noreferrer"
            className="tc-li"
            title="Connect on LinkedIn"
          >
            <LinkedInIcon />
            LinkedIn
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
function Trainers() {
  const navigate = useNavigate();

  const wrapperRef  = useRef(null);
  const trackRef    = useRef(null);
  const intervalRef = useRef(null);
  const isPaused    = useRef(false);
  const isJumping   = useRef(false);

  // touch/swipe refs (mobile)
  const touchStartX = useRef(0);
  const touchDeltaX  = useRef(0);
  const isDragging   = useRef(false);

  // mouse-drag refs (desktop) — mirrors touch logic
  const mouseStartX    = useRef(0);
  const mouseDeltaX     = useRef(0);
  const isMouseDragging = useRef(false);

  const [cardW, setCardW]   = useState(0);
  const [visible, setVisible] = useState(
    typeof window !== "undefined" ? getVisibleCount(window.innerWidth) : 4
  );
  const [gap, setGap] = useState(
    typeof window !== "undefined" ? getGap(window.innerWidth) : 20
  );
  // side padding state (mobile/tablet only, desktop = 0 = unchanged)
  const [sidePad, setSidePad] = useState(
    typeof window !== "undefined" ? getSidePad(window.innerWidth) : 0
  );
  const [isGrabbing, setIsGrabbing] = useState(false);

  // A click on the plus-card should only navigate if the user wasn't
  // dragging the carousel — small drags on mouse-up would otherwise also
  // fire as a click.
  const dragMoved = useRef(false);
  const handleOpenApplication = useCallback(() => {
    if (dragMoved.current) {
      dragMoved.current = false;
      return;
    }
    navigate("/trainer-application");
  }, [navigate]);

  const cloneCount = visible;
  const step = cardW + gap;

  // Clone layout: [ last N clones ][ original 6 ][ first N clones ]
  const extendedCards = [
    ...allCards.slice(-cloneCount),
    ...allCards,
    ...allCards.slice(0, cloneCount),
  ];

  const [index, setIndex] = useState(cloneCount);

  const moveTo = useCallback(
    (idx, animated = true) => {
      const track = trackRef.current;
      if (!track || cardW === 0) return;
      track.style.transition = animated
        ? "transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        : "none";
      track.style.transform = `translateX(-${idx * step}px)`;
      setIndex(idx);
    },
    [step, cardW]
  );

  // Recalculate visible count, gap, side padding & card width on resize
  // (mobile/tablet responsive). NOTE: containerW is measured on wrapperRef,
  // which sits INSIDE the padded section, so it already reflects the
  // padded, available width — card width math stays correct.
  useEffect(() => {
    const calcWidth = () => {
      if (!wrapperRef.current) return;
      const w = window.innerWidth;
      const newVisible = getVisibleCount(w);
      const newGap = getGap(w);
      const newSidePad = getSidePad(w);
      const containerW = wrapperRef.current.offsetWidth - newSidePad * 2;
      const newCardW = Math.floor((containerW - newGap * (newVisible - 1)) / newVisible);

      setVisible((prev) => (prev !== newVisible ? newVisible : prev));
      setGap(newGap);
      setSidePad(newSidePad);
      setCardW(newCardW);
    };
    calcWidth();
    window.addEventListener("resize", calcWidth);
    return () => window.removeEventListener("resize", calcWidth);
  }, []);

  // Whenever visible count changes (breakpoint crossed), snap back to a clean start
  useEffect(() => {
    if (cardW > 0) moveTo(cloneCount, false);
  }, [visible]); // eslint-disable-line

  // Boundary pe silently jump karo
  const handleTransitionEnd = useCallback(() => {
    if (isJumping.current) return;
    let newIdx = null;
    if (index >= cloneCount + total) newIdx = cloneCount;
    else if (index < cloneCount) newIdx = cloneCount + total - 1;

    if (newIdx !== null) {
      isJumping.current = true;
      moveTo(newIdx, false);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (trackRef.current)
            trackRef.current.style.transition =
              "transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
          isJumping.current = false;
        })
      );
    }
  }, [index, cloneCount, moveTo]);

  const next = useCallback(() => {
    setIndex((prev) => {
      const n = prev + 1;
      moveTo(n);
      return n;
    });
  }, [moveTo]);

  const prev = useCallback(() => {
    setIndex((p) => {
      const n = p - 1;
      moveTo(n);
      return n;
    });
  }, [moveTo]);

  const startAutoplay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!isPaused.current) next();
    }, DELAY);
  }, [next]);

  const stopAutoplay = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  // cardW ready hone pe initial position set karo
  useEffect(() => {
    if (cardW > 0) moveTo(cloneCount, false);
    // eslint-disable-next-line
  }, [cardW === 0]);

  // cardW ready hone pe autoplay shuru karo
  useEffect(() => {
    if (cardW > 0) startAutoplay();
    return () => stopAutoplay();
  }, [cardW]); // eslint-disable-line

  const handleMouseEnter = () => {
    isPaused.current = true;
    stopAutoplay();
  };

  const handleMouseLeaveWrapper = () => {
    // if user drags out of the wrapper while holding the mouse down,
    // treat it the same as a mouse-up so the drag ends cleanly
    if (isMouseDragging.current) {
      handleMouseUp();
    }
    isPaused.current = false;
    startAutoplay();
  };

  // ─── Touch / swipe support (mobile) ────────────────────────────────────
  const handleTouchStart = (e) => {
    isPaused.current = true;
    isDragging.current = true;
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    if (trackRef.current) trackRef.current.style.transition = "none";
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || !trackRef.current) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    if (Math.abs(touchDeltaX.current) > 6) dragMoved.current = true;
    trackRef.current.style.transform = `translateX(${-index * step + touchDeltaX.current}px)`;
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    isPaused.current = false;

    const threshold = step * 0.2;
    if (touchDeltaX.current < -threshold) {
      next();
    } else if (touchDeltaX.current > threshold) {
      prev();
    } else {
      moveTo(index, true);
    }
    touchDeltaX.current = 0;
    startAutoplay();
  };

  // ─── Mouse-drag support (desktop) — mirrors touch behaviour ───────────
  const handleMouseDown = (e) => {
    isPaused.current = true;
    isMouseDragging.current = true;
    setIsGrabbing(true);
    mouseStartX.current = e.clientX;
    mouseDeltaX.current = 0;
    if (trackRef.current) trackRef.current.style.transition = "none";
  };

  const handleMouseMoveDrag = (e) => {
    if (!isMouseDragging.current || !trackRef.current) return;
    mouseDeltaX.current = e.clientX - mouseStartX.current;
    if (Math.abs(mouseDeltaX.current) > 6) dragMoved.current = true;
    trackRef.current.style.transform = `translateX(${-index * step + mouseDeltaX.current}px)`;
  };

  const handleMouseUp = () => {
    if (!isMouseDragging.current) return;
    isMouseDragging.current = false;
    setIsGrabbing(false);
    isPaused.current = false;

    const threshold = step * 0.2;
    if (mouseDeltaX.current < -threshold) {
      next();
    } else if (mouseDeltaX.current > threshold) {
      prev();
    } else {
      moveTo(index, true);
    }
    mouseDeltaX.current = 0;
    startAutoplay();
  };

  const dotIndex = ((index - cloneCount) % total + total) % total;

  return (
    <section className="trainers" id="trainers">
      <div className="tr-intro">
        <div>
          <div className="eyebrow rv">
            <div className="eyebrow-dash"></div>
            <span className="eyebrow-text">Faculty</span>
          </div>
          <h2
            className="sec-title rv d1"
            style={{ marginBottom: "-20px", lineHeight: "1.1" }}
          >
            <span style={{ color: "#1e1d1b" }}> Trained by </span>
            <em>Industry Experts,</em>
          </h2>
        </div>
        <p
          style={{
            color: "#6b7280",
            fontSize: "16px",
            lineHeight: "1.5",
            fontWeight: "300",
            marginTop: 0,
            marginBottom: "-10px",
          }}
        >
          Every trainer is a working professional — architects, project managers,
          and BIM specialists who've shipped real projects with top Indian and
          international firms. No ivory towers. Just direct, applied knowledge
          that transforms careers.
        </p>
      </div>

      {/* Overflow wrapper */}
      <div
        ref={wrapperRef}
        className="rv"
        style={{
          overflow: "hidden",
          width: "100%",
          background: "transparent",
          paddingLeft: `${sidePad}px`,
          paddingRight: `${sidePad}px`,
          boxSizing: "border-box",
          cursor: isGrabbing ? "grabbing" : "grab",
          userSelect: "none",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeaveWrapper}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMoveDrag}
        onMouseUp={handleMouseUp}
        onPointerEnter={handleMouseEnter}
        onPointerLeave={handleMouseLeaveWrapper}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Sliding track */}
        <div
          ref={trackRef}
          onTransitionEnd={handleTransitionEnd}
          style={{ display: "flex", gap: `${gap}px`, willChange: "transform" }}
        >
          {extendedCards.map((t, i) => (
            <TrainerCard key={i} t={t} cardW={cardW} onOpenApplication={handleOpenApplication} />
          ))}
        </div>
      </div>

      {/* Dot indicators */}
      <div
        style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "50px" }}
      >
        {allCards.map((_, i) => (
          <button
            key={i}
            onClick={() => moveTo(cloneCount + i)}
            aria-label={`Slide ${i + 1}`}
            style={{
              width: i === dotIndex ? "20px" : "8px",
              height: "8px",
              borderRadius: "4px",
              border: "none",
              cursor: "pointer",
              padding: 0,
              transition: "width 0.3s ease, background 0.3s ease",
              background: i === dotIndex ? "#c84b20" : "#d1d5db",
            }}
          />
        ))}
      </div>
    </section>
  );
}

export default Trainers;